import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiScreeningMatchProvider, customListApi, ResponseError } from '../apis';
import { InternalApiCustomList } from '../api-internal';
import { MenuItem, SalvButton, SalvMenuButton, SalvTable, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import { faTriangleExclamation, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router';
import Has from '../auth/Has';
import { CAN_ADD_CUSTOM_LIST, CAN_ARCHIVE_CUSTOM_LIST, CAN_READ_CUSTOM_LIST_RECORD } from '../auth/permissions';
import SalvPage from '../common/SalvPage';
import { getConfigurationUrl, getCustomListPath, InternalNavigation } from '../navigation/helper';
import { ConfigurationProduct } from '../settings/configuration/Configuration';
import { createColumnHelper } from '@tanstack/react-table';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { showFeedback } from '../feedback/feedbackReducer';
import { useMe } from '../organisation/useMe';
import { useDispatch } from 'react-redux';
import { parseErrorMessage } from '../common/errors/helpers';
import { useCustomListSuiWarnings } from './significantFields/useCustomListSuiWarnings';

export const CustomLists = () => {
  const { permissions } = useMe();
  const [lists, setLists] = useState<InternalApiCustomList[]>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const listIds = useMemo(() => (lists ?? []).map((list) => list.id!), [lists]);
  const { isLoading: isSuiLoading, listIdsWithMissingSui } = useCustomListSuiWarnings(listIds);

  useEffect(() => {
    const fetchLists = async () => {
      const lists = await customListApi.getLists().then((lists) => {
        return lists.filter((list) => !list.code?.startsWith(ApiScreeningMatchProvider.DOW_JONES));
      });
      setLists(lists);
    };

    fetchLists();
  }, []);

  const handleArchive = async (list: InternalApiCustomList) => {
    const isConfirmed = await dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        description: `"${list.name}" will not be available for use`,
        confirmationText: 'Yes, archive',
      })
    );

    if (!isConfirmed) {
      return;
    }

    try {
      await customListApi.archiveList({ customListId: list.id! });
      setLists((prevLists) => prevLists?.filter((l) => l.id !== list.id));
      dispatch(
        showFeedback({
          message: `Custom list "${list.name}" was successfully archived`,
          success: true,
        })
      );
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        return;
      }

      const { status } = error.response;
      if (status === 400) {
        dispatch(
          showConfirmationModal({
            title: 'Archiving cannot be completed',
            description: await parseErrorMessage(error.response),
            onConfirm: null,
          })
        );
      }
    }
  };

  const columnHelper = createColumnHelper<InternalApiCustomList>();

  const getColumns = useCallback(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ row }) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {row.original.name}
            {!isSuiLoading && listIdsWithMissingSui.has(row.original.id!) && (
              <SalvTooltipWrapper
                tooltip='Significant Update Indicators are not configured for this list'
                placement='top'
              >
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  style={{ color: 'var(--danger-default)' }}
                  aria-label='Significant Update Indicators are not configured for this list'
                />
              </SalvTooltipWrapper>
            )}
          </span>
        ),
      }),
      columnHelper.accessor('description', {
        header: 'Description',
      }),
      columnHelper.accessor('createdBy', {
        header: 'Created by',
      }),
      columnHelper.accessor('updatedBy', {
        header: 'Updated by',
      }),
      columnHelper.display({
        id: 'actions',
        size: 0,
        cell: ({ row }) => {
          return (
            <Has allPermissions={[CAN_READ_CUSTOM_LIST_RECORD]}>
              <SalvMenuButton
                size={Size.SMALL}
                placement={'bottom end'}
                ariaLabel={`Actions for ${row.original.name}`}
                onAction={async ([action]) => {
                  if (action === 'view') {
                    navigate(getCustomListPath(row.original.id!));
                  } else if (action === 'archive') {
                    await handleArchive(row.original);
                  }
                }}
              >
                <MenuItem title={'View'} value={'view'} />
                <MenuItem
                  title={'Archive'}
                  value={'archive'}
                  dangerTextColor
                  disabled={!permissions.includes(CAN_ARCHIVE_CUSTOM_LIST)}
                />
              </SalvMenuButton>
            </Has>
          );
        },
      }),
    ],
    [navigate, handleArchive, isSuiLoading, listIdsWithMissingSui]
  );

  return (
    <SalvPage
      title={'Custom Lists'}
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.GENERAL),
      }}
      actions={
        <Has allPermissions={[CAN_ADD_CUSTOM_LIST]}>
          <SalvButton
            label='New list'
            icon={faPlus}
            size={Size.SMALL}
            onClick={() => navigate(InternalNavigation.customListAdd)}
          />
        </Has>
      }
    >
      <SalvTable
        data={lists ?? []}
        columns={getColumns()}
        emptyMessage='No Custom lists yet'
        isLoading={!lists || isSuiLoading}
        hover={permissions.includes(CAN_READ_CUSTOM_LIST_RECORD)}
        rowClick={
          permissions.includes(CAN_READ_CUSTOM_LIST_RECORD)
            ? (row) => navigate(getCustomListPath(row.original.id!))
            : undefined
        }
      />
    </SalvPage>
  );
};

export default CustomLists;
